"use client"

import Image from "next/image"
import "swiper/css"
import "swiper/css/pagination"
import { Swiper, SwiperSlide } from "swiper/react"
import { FreeMode, Pagination } from "swiper/modules"
import type { components } from "umbraco"
import { getMediaUrl } from "umbraco"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
import Container from "../../Container"
import SectionTitleDecoration from "../../icons/SectionTitleDecoration"
import {
  AvatarDesktopLayer,
  AvatarMobileLayer,
  AvatarPlaceholder,
  AvatarWrapper,
  Card,
  CardContent,
  CardTitleBlock,
  ChevronWrapper,
  EmployeeName,
  EmployeeRole,
  HeaderSection,
  Root,
  Subtitle,
  SwiperWrapper,
  TestimonialText,
  Title,
  TitleRow,
} from "./styles"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type EmployeesTestimonialsData =
  components["schemas"]["EmployeesTestimonialsBlockPropertiesModel"]
type EmployeeTestimonialItemData =
  components["schemas"]["EmployeeTestimonialItemPropertiesModel"]

interface Props {
  data: EmployeesTestimonialsData
  settings?: Record<string, unknown> | null
  locale: string
  culture: string
  [key: string]: unknown
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
// EGDE-597: the avatar read only `desktopImage`, so on mobile the editor's
// Mobile Image (portrait crop) never appeared — the desktop asset was shown at
// every viewport. Mobile falls back to the desktop asset when no mobile crop is
// authored, matching the CMS field description.
function getImageUrls(blockList: unknown): {
  desktop: string | null
  mobile: string | null
} {
  const list = blockList as
    | { items?: Array<{ content: { properties?: Record<string, unknown> } }> }
    | undefined
  const item = list?.items?.[0]
  const props = item?.content?.properties
  if (!props) return { desktop: null, mobile: null }

  const desktopRel = getMediaUrl(props.desktopImage as unknown)
  const mobileRel = getMediaUrl(props.mobileImage as unknown)
  const desktop = desktopRel ? getUmbracoMediaUrl(desktopRel) : null
  return {
    desktop,
    mobile: mobileRel ? getUmbracoMediaUrl(mobileRel) : desktop,
  }
}

// ---------------------------------------------------------------------------
// Card sub-component
// ---------------------------------------------------------------------------
function TestimonialCard({ item }: { item: EmployeeTestimonialItemData }) {
  const { desktop: desktopUrl, mobile: mobileUrl } = getImageUrls(item.image)

  return (
    <Card>
      <AvatarWrapper>
        {desktopUrl ? (
          <>
            <AvatarDesktopLayer>
              <Image
                src={desktopUrl}
                alt={item.employeeName ?? ""}
                fill
                sizes="120px"
                style={{ objectFit: "cover" }}
              />
            </AvatarDesktopLayer>
            <AvatarMobileLayer>
              <Image
                src={mobileUrl ?? desktopUrl}
                alt={item.employeeName ?? ""}
                fill
                sizes="120px"
                style={{ objectFit: "cover" }}
              />
            </AvatarMobileLayer>
          </>
        ) : (
          <AvatarPlaceholder aria-hidden="true">
            {/* EGDE-81: exact Figma placeholder glyph (Property_1_4.svg, node
                40013165-24018). The dotted-person icon is built from discrete
                round dots — NOT dashed strokes — and is rendered at the full
                120x120 of the avatar circle so its scale matches Figma exactly.
                The first path includes the #F1F1F1 background circle (same as
                the neutral200 wrapper); the dots are #6D6D6D. */}
            <svg
              width="120"
              height="120"
              viewBox="0 0 120 120"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M60.0008 4.79688C29.5616 4.79688 4.80078 29.5577 4.80078 59.9969C4.80078 90.4361 29.5616 115.197 60.0008 115.197C90.44 115.197 115.201 90.4361 115.201 59.9969C115.201 29.5577 90.44 4.79688 60.0008 4.79688ZM76.8008 33.5969C78.1256 33.5969 79.2008 34.6721 79.2008 35.9969C79.2008 37.3217 78.1256 38.3969 76.8008 38.3969C75.476 38.3969 74.4008 37.3217 74.4008 35.9969C74.4008 34.6721 75.476 33.5969 76.8008 33.5969ZM69.6008 26.3969C70.9256 26.3969 72.0008 27.4721 72.0008 28.7969C72.0008 30.1217 70.9256 31.1969 69.6008 31.1969C68.276 31.1969 67.2008 30.1217 67.2008 28.7969C67.2008 27.4721 68.276 26.3969 69.6008 26.3969ZM43.2008 33.5969C44.5256 33.5969 45.6008 34.6721 45.6008 35.9969C45.6008 37.3217 44.5256 38.3969 43.2008 38.3969C41.876 38.3969 40.8008 37.3217 40.8008 35.9969C40.8008 34.6721 41.876 33.5969 43.2008 33.5969ZM40.8008 43.1969C42.1256 43.1969 43.2008 44.2721 43.2008 45.5969C43.2008 46.9217 42.1256 47.9969 40.8008 47.9969C39.476 47.9969 38.4008 46.9217 38.4008 45.5969C38.4008 44.2721 39.476 43.1969 40.8008 43.1969ZM26.4008 91.1969C25.076 91.1969 24.0008 90.1217 24.0008 88.7969C24.0008 87.4721 25.076 86.3969 26.4008 86.3969C27.7256 86.3969 28.8008 87.4721 28.8008 88.7969C28.8008 90.1217 27.7256 91.1969 26.4008 91.1969ZM32.6624 82.0049C32.2088 82.6289 31.4888 82.9625 30.7664 82.9625C30.2384 82.9625 29.7344 82.7969 29.3048 82.4585C28.2488 81.6689 28.0544 80.1545 28.8488 79.1009C29.6408 78.0449 31.1528 77.8505 32.2088 78.6449C33.2624 79.4585 33.4808 80.9465 32.6624 82.0049ZM38.8544 76.0049C38.4224 76.3169 37.9184 76.4585 37.4384 76.4585C36.6944 76.4585 35.9768 76.1225 35.4944 75.4769C34.7024 74.4209 34.9424 72.9089 35.9984 72.1169L36.0224 72.1409C37.0784 71.3489 38.5904 71.5649 39.3584 72.6449C40.1504 73.7009 39.9128 75.2129 38.8544 76.0049ZM40.8008 55.1969C40.8008 53.8721 41.876 52.7969 43.2008 52.7969C44.5256 52.7969 45.6008 53.8721 45.6008 55.1969C45.6008 56.5217 44.5256 57.5969 43.2008 57.5969C41.876 57.5969 40.8008 56.5217 40.8008 55.1969ZM45.6008 71.9969C44.276 71.9969 43.2008 70.9217 43.2008 69.5969C43.2008 68.2721 44.276 67.1969 45.6008 67.1969C46.9256 67.1969 48.0008 68.2721 48.0008 69.5969C48.0008 70.9217 46.9256 71.9969 45.6008 71.9969ZM50.4008 64.7969C49.076 64.7969 48.0008 63.7217 48.0008 62.3969C48.0008 61.0721 49.076 59.9969 50.4008 59.9969C51.7256 59.9969 52.8008 61.0721 52.8008 62.3969C52.8008 63.7217 51.7256 64.7969 50.4008 64.7969ZM50.4008 31.1969C49.076 31.1969 48.0008 30.1217 48.0008 28.7969C48.0008 27.4721 49.076 26.3969 50.4008 26.3969C51.7256 26.3969 52.8008 27.4721 52.8008 28.7969C52.8008 30.1217 51.7256 31.1969 50.4008 31.1969ZM60.0008 67.1969C58.676 67.1969 57.6008 66.1217 57.6008 64.7969C57.6008 63.4721 58.676 62.3969 60.0008 62.3969C61.3256 62.3969 62.4008 63.4721 62.4008 64.7969C62.4008 66.1217 61.3256 67.1969 60.0008 67.1969ZM60.0008 28.7969C58.676 28.7969 57.6008 27.7217 57.6008 26.3969C57.6008 25.0721 58.676 23.9969 60.0008 23.9969C61.3256 23.9969 62.4008 25.0721 62.4008 26.3969C62.4008 27.7217 61.3256 28.7969 60.0008 28.7969ZM69.6008 64.7969C68.276 64.7969 67.2008 63.7217 67.2008 62.3969C67.2008 61.0721 68.276 59.9969 69.6008 59.9969C70.9256 59.9969 72.0008 61.0721 72.0008 62.3969C72.0008 63.7217 70.9256 64.7969 69.6008 64.7969ZM74.4008 71.9969C73.076 71.9969 72.0008 70.9217 72.0008 69.5969C72.0008 68.2721 73.076 67.1969 74.4008 67.1969C75.7256 67.1969 76.8008 68.2721 76.8008 69.5969C76.8008 70.9217 75.7256 71.9969 74.4008 71.9969ZM76.8008 57.5969C75.476 57.5969 74.4008 56.5217 74.4008 55.1969C74.4008 53.8721 75.476 52.7969 76.8008 52.7969C78.1256 52.7969 79.2008 53.8721 79.2008 55.1969C79.2008 56.5217 78.1256 57.5969 76.8008 57.5969ZM76.8008 45.5969C76.8008 44.2721 77.876 43.1969 79.2008 43.1969C80.5256 43.1969 81.6008 44.2721 81.6008 45.5969C81.6008 46.9217 80.5256 47.9969 79.2008 47.9969C77.876 47.9969 76.8008 46.9217 76.8008 45.5969ZM84.5048 75.4769C84.0248 76.1249 83.3048 76.4585 82.5584 76.4585C82.0808 76.4585 81.5768 76.3145 81.1424 76.0049C80.0888 75.2129 79.8464 73.7249 80.6384 72.6449C81.4064 71.5649 82.9184 71.3489 83.9768 72.1409C85.0544 72.9089 85.2968 74.4209 84.5048 75.4769ZM89.2328 82.9625C88.5128 82.9625 87.7928 82.6265 87.3368 82.0049C86.5208 80.9489 86.7368 79.4369 87.7928 78.6449C88.8464 77.8529 90.3584 78.0449 91.1504 79.1009C91.9424 80.1545 91.7504 81.6689 90.6968 82.4585C90.2624 82.7969 89.7368 82.9625 89.2328 82.9625ZM93.6008 91.1969C92.276 91.1969 91.2008 90.1217 91.2008 88.7969C91.2008 87.4721 92.276 86.3969 93.6008 86.3969C94.9256 86.3969 96.0008 87.4721 96.0008 88.7969C96.0008 90.1217 94.9256 91.1969 93.6008 91.1969Z"
                fill="#F1F1F1"
              />
              <path
                d="M76.8 33.6C78.1248 33.6 79.2 34.6752 79.2 36C79.2 37.3248 78.1248 38.4 76.8 38.4C75.4752 38.4 74.4 37.3248 74.4 36C74.4 34.6752 75.4752 33.6 76.8 33.6ZM69.6 26.4C70.9248 26.4 72 27.4752 72 28.8C72 30.1248 70.9248 31.2 69.6 31.2C68.2752 31.2 67.2 30.1248 67.2 28.8C67.2 27.4752 68.2752 26.4 69.6 26.4ZM43.2 33.6C44.5248 33.6 45.6 34.6752 45.6 36C45.6 37.3248 44.5248 38.4 43.2 38.4C41.8752 38.4 40.8 37.3248 40.8 36C40.8 34.6752 41.8752 33.6 43.2 33.6ZM40.8 43.2C42.1248 43.2 43.2 44.2752 43.2 45.6C43.2 46.9248 42.1248 48 40.8 48C39.4752 48 38.4 46.9248 38.4 45.6C38.4 44.2752 39.4752 43.2 40.8 43.2ZM26.4 91.2C25.0752 91.2 24 90.1248 24 88.8C24 87.4752 25.0752 86.4 26.4 86.4C27.7248 86.4 28.8 87.4752 28.8 88.8C28.8 90.1248 27.7248 91.2 26.4 91.2ZM32.6616 82.008C32.208 82.632 31.488 82.9656 30.7656 82.9656C30.2376 82.9656 29.7336 82.8 29.304 82.4616C28.248 81.672 28.0536 80.1576 28.848 79.104C29.64 78.048 31.152 77.8536 32.208 78.648C33.2616 79.4616 33.48 80.9496 32.6616 82.008ZM38.8536 76.008C38.4216 76.32 37.9176 76.4616 37.4376 76.4616C36.6936 76.4616 35.976 76.1256 35.4936 75.48C34.7016 74.424 34.9416 72.912 35.9976 72.12L36.0216 72.144C37.0776 71.352 38.5896 71.568 39.3576 72.648C40.1496 73.704 39.912 75.216 38.8536 76.008ZM40.8 55.2C40.8 53.8752 41.8752 52.8 43.2 52.8C44.5248 52.8 45.6 53.8752 45.6 55.2C45.6 56.5248 44.5248 57.6 43.2 57.6C41.8752 57.6 40.8 56.5248 40.8 55.2ZM45.6 72C44.2752 72 43.2 70.9248 43.2 69.6C43.2 68.2752 44.2752 67.2 45.6 67.2C46.9248 67.2 48 68.2752 48 69.6C48 70.9248 46.9248 72 45.6 72ZM50.4 64.8C49.0752 64.8 48 63.7248 48 62.4C48 61.0752 49.0752 60 50.4 60C51.7248 60 52.8 61.0752 52.8 62.4C52.8 63.7248 51.7248 64.8 50.4 64.8ZM50.4 31.2C49.0752 31.2 48 30.1248 48 28.8C48 27.4752 49.0752 26.4 50.4 26.4C51.7248 26.4 52.8 27.4752 52.8 28.8C52.8 30.1248 51.7248 31.2 50.4 31.2ZM60 67.2C58.6752 67.2 57.6 66.1248 57.6 64.8C57.6 63.4752 58.6752 62.4 60 62.4C61.3248 62.4 62.4 63.4752 62.4 64.8C62.4 66.1248 61.3248 67.2 60 67.2ZM60 28.8C58.6752 28.8 57.6 27.7248 57.6 26.4C57.6 25.0752 58.6752 24 60 24C61.3248 24 62.4 25.0752 62.4 26.4C62.4 27.7248 61.3248 28.8 60 28.8ZM69.6 64.8C68.2752 64.8 67.2 63.7248 67.2 62.4C67.2 61.0752 68.2752 60 69.6 60C70.9248 60 72 61.0752 72 62.4C72 63.7248 70.9248 64.8 69.6 64.8ZM74.4 72C73.0752 72 72 70.9248 72 69.6C72 68.2752 73.0752 67.2 74.4 67.2C75.7248 67.2 76.8 68.2752 76.8 69.6C76.8 70.9248 75.7248 72 74.4 72ZM76.8 57.6C75.4752 57.6 74.4 56.5248 74.4 55.2C74.4 53.8752 75.4752 52.8 76.8 52.8C78.1248 52.8 79.2 53.8752 79.2 55.2C79.2 56.5248 78.1248 57.6 76.8 57.6ZM76.8 45.6C76.8 44.2752 77.8752 43.2 79.2 43.2C80.5248 43.2 81.6 44.2752 81.6 45.6C81.6 46.9248 80.5248 48 79.2 48C77.8752 48 76.8 46.9248 76.8 45.6ZM84.504 75.48C84.024 76.128 83.304 76.4616 82.5576 76.4616C82.08 76.4616 81.576 76.3176 81.1416 76.008C80.088 75.216 79.8456 73.728 80.6376 72.648C81.4056 71.568 82.9176 71.352 83.976 72.144C85.0536 72.912 85.296 74.424 84.504 75.48ZM89.232 82.9656C88.512 82.9656 87.792 82.6296 87.336 82.008C86.52 80.952 86.736 79.44 87.792 78.648C88.8456 77.856 90.3576 78.048 91.1496 79.104C91.9416 80.1576 91.7496 81.672 90.696 82.4616C90.2616 82.8 89.736 82.9656 89.232 82.9656ZM93.6 91.2C92.2752 91.2 91.2 90.1248 91.2 88.8C91.2 87.4752 92.2752 86.4 93.6 86.4C94.9248 86.4 96 87.4752 96 88.8C96 90.1248 94.9248 91.2 93.6 91.2Z"
                fill="#6D6D6D"
              />
            </svg>
          </AvatarPlaceholder>
        )}
      </AvatarWrapper>
      <CardContent>
        <CardTitleBlock>
          {item.employeeName && (
            <EmployeeName variant="body1">{item.employeeName}</EmployeeName>
          )}
          {item.role && (
            <EmployeeRole variant="body2">{item.role}</EmployeeRole>
          )}
        </CardTitleBlock>
        {item.testimonialText && (
          <TestimonialText variant="body2">
            {item.testimonialText}
          </TestimonialText>
        )}
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
export default function EmployeesTestimonials({ data, culture }: Props) {
  const isRtl = culture === "he-IL" || culture === "ar"
  const items = data.items?.items ?? []

  const parsedItems = items.map((block) => {
    const props = block.content.properties as
      | EmployeeTestimonialItemData
      | undefined
    return { id: block.content.id, props }
  })

  return (
    <Container paddingMobile>
      <Root>
        <HeaderSection>
          <TitleRow>
            <ChevronWrapper>
              {/* EGDE-289: mirror the RTL-drawn chevron on LTR pages. */}
              <SectionTitleDecoration
                style={isRtl ? undefined : { transform: "scaleX(-1)" }}
              />
            </ChevronWrapper>
            <Title variant="h2">{data.sectionTitle}</Title>
          </TitleRow>
          {data.sectionSubtitle && (
            <Subtitle variant="body1">{data.sectionSubtitle}</Subtitle>
          )}
        </HeaderSection>

        <SwiperWrapper>
          <Swiper
            modules={[FreeMode, Pagination]}
            slidesPerView={1.15}
            spaceBetween={16}
            freeMode={{ enabled: true, momentumRatio: 0.5 }}
            pagination={{ clickable: true }}
            breakpoints={{
              768: { slidesPerView: 2.2, spaceBetween: 16 },
              1280: {
                slidesPerView: 3,
                spaceBetween: 32,
                freeMode: { enabled: false },
              },
            }}
          >
            {parsedItems.map((item, index) =>
              item.props ? (
                <SwiperSlide key={item.id ?? index}>
                  <TestimonialCard item={item.props} />
                </SwiperSlide>
              ) : null,
            )}
          </Swiper>
        </SwiperWrapper>
      </Root>
    </Container>
  )
}
